import React, { useEffect, useState } from "react";
import { CheckCircle2, Ruler, Save } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Sheet, SheetContent, SheetDescription, SheetFooter, SheetHeader, SheetTitle } from "@/components/ui/sheet";

const GROUPS = [
  ["Cabinet depths", [["base_depth", "Base"], ["upper_depth", "Upper"], ["tall_depth", "Tall / pantry"], ["drawer_bank_depth", "Drawer bank"]]],
  ["Vertical layout", [["base_box_height", "Base box height"], ["tall_height", "Tall cabinet height"], ["upper_bottom_height", "Upper cabinet bottom"], ["ceiling_height", "Ceiling height"]]],
  ["Construction details", [["countertop_thickness", "Countertop thickness"], ["toe_kick_height", "Toe-kick height"], ["toe_kick_recess", "Toe-kick recess"]]],
];

export const CabinetParametersSheet = ({ open, onOpenChange, parameters, onSave }) => {
  const [draft, setDraft] = useState(parameters);
  useEffect(() => setDraft(parameters), [parameters, open]);
  const set = (key, value) => setDraft(current => ({ ...current, [key]: Number(value), confirmed: false }));
  if (!draft) return null;
  return <Sheet open={open} onOpenChange={onOpenChange}>
    <SheetContent className="w-full overflow-y-auto sm:max-w-xl" data-testid="cabinet-parameters-sheet">
      <SheetHeader>
        <div className="flex h-11 w-11 items-center justify-center bg-slate-950 text-white"><Ruler className="h-5 w-5" /></div>
        <SheetTitle>Cabinet engineering parameters</SheetTitle>
        <SheetDescription>These standards fill unreadable dimensions. Individual cabinet overrides remain available in the cabinet editor.</SheetDescription>
      </SheetHeader>
      <div className="mt-7 space-y-7">
        {GROUPS.map(([title, fields]) => <section key={title} className="border-t border-slate-200 pt-4">
          <h3 className="text-sm font-bold text-slate-950">{title}</h3>
          <div className="mt-3 grid grid-cols-2 gap-4">
            {fields.map(([key, label]) => <div key={key}>
              <Label htmlFor={`parameter-${key}`} className="text-xs">{label}</Label>
              <div className="relative mt-1"><Input id={`parameter-${key}`} type="number" min="0" step="0.5" value={draft[key]} onChange={event => set(key, event.target.value)} className="pr-9 font-mono" data-testid={`parameter-${key}-input`} /><span className="pointer-events-none absolute right-3 top-2.5 text-xs text-slate-400">in</span></div>
            </div>)}
          </div>
        </section>)}
        <label className={`flex cursor-pointer items-start gap-3 border p-4 ${draft.confirmed ? "border-emerald-300 bg-emerald-50" : "border-amber-300 bg-amber-50"}`} data-testid="parameters-confirmation-control">
          <Checkbox checked={Boolean(draft.confirmed)} onCheckedChange={checked => setDraft(current => ({ ...current, confirmed: Boolean(checked) }))} data-testid="parameters-confirmed-checkbox" />
          <span><strong className="flex items-center gap-2 text-sm text-slate-950"><CheckCircle2 className="h-4 w-4" />I reviewed these shop standards</strong><small className="mt-1 block text-xs leading-relaxed text-slate-600">Required before final layout approval and CNC handoff. Preliminary concepts can be generated first.</small></span>
        </label>
      </div>
      <SheetFooter className="mt-8"><Button type="button" onClick={() => { onSave(draft); onOpenChange(false); }} data-testid="save-cabinet-parameters-button"><Save className="mr-2 h-4 w-4" />Apply parameters</Button></SheetFooter>
    </SheetContent>
  </Sheet>;
};